import { match } from '@frontend/duck-tape';
import { usePaymentStepContext, usePaywallContext } from '@frontend/react-domain';
import { CardContainer, Icon, Text, XStack, YStack } from '@frontend/web-react';
import { StripeProvider } from '@frontend/web/components';
import { usePricingTerminology } from '@frontend/web/context';
import { CreditCardInput } from '../../CreditCardInput';
import { PurchaseDetailsCard } from '../../PurchaseDetailsCard';
import { PaywallExpressCheckout } from './PaywallExpressCheckout';

const PaymentStepComponent = () => {
  const { paywallState } = usePaywallContext();
  const {
    completePaymentAfterWalletConfirm,
    setCardCvcInfo,
    setCardExpiryInfo,
    setCardNumberInfo,
    stripeClientSecret,
  } = usePaymentStepContext();
  const { pricingTerminology, showCredits } = usePricingTerminology();

  const firstPurchaseData = paywallState.purchase;
  const promotionTranslation = paywallState.promotionTranslation;
  const selectedPurchaseType = firstPurchaseData.selectedPurchaseType;

  return (
    <YStack gapY="md">
      {firstPurchaseData.selectedPurchaseType
        ? match(firstPurchaseData.selectedPurchaseType)
            .with('Subscription', () => (
              <XStack alignItems="center" gapX="md">
                <Icon name="IconRotate" size={24} />
                <Text type="h2">Your subscription</Text>
              </XStack>
            ))
            .with('ALaCarteCredits', () => (
              <XStack alignItems="center" gapX="md">
                <Icon name="IconShoppingBag" size={24} />
                <Text type="h2">
                  {showCredits ? `Your ${pricingTerminology.unit_term_plural} pack` : 'Your purchase'}
                </Text>
              </XStack>
            ))
            .otherwise(() => null)
        : null}

      {promotionTranslation.promoCode && selectedPurchaseType === 'Subscription' ? (
        <YStack gapY="sm">
          <Text type="h6">Promotion</Text>
          <CardContainer>
            <YStack gapY="xs">
              <Text type="h5">{promotionTranslation.promotionName}</Text>
              <Text className="italic" type="p-sm">
                {promotionTranslation.promotionDescription}
              </Text>
            </YStack>
          </CardContainer>
        </YStack>
      ) : null}

      {firstPurchaseData ? <PurchaseDetailsCard firstPurchaseData={firstPurchaseData} /> : null}

      <YStack gapY="md">
        <XStack alignItems="center" gapX="sm">
          <Icon name="IconCreditCard" size={20} />
          <Text type="h2">Add a payment method</Text>
        </XStack>

        <YStack gapY="sm">
          <Text type="p-sm">
            {selectedPurchaseType === 'Subscription'
              ? 'We will use to charge your monthly subscription.'
              : `Add a payment method to purchase ${pricingTerminology.unit_term_plural}.`}
          </Text>

          <StripeProvider expectedElements={[]} options={{ currency: 'usd', mode: 'setup' }}>
            <PaywallExpressCheckout clientSecret={stripeClientSecret} onComplete={completePaymentAfterWalletConfirm} />
          </StripeProvider>

          <CreditCardInput
            setCardCvcInfo={setCardCvcInfo || (() => {})}
            setCardExpiryInfo={setCardExpiryInfo || (() => {})}
            setCardNumberInfo={setCardNumberInfo || (() => {})}
          />

          <Text className="italic" type="caption">
            Duckbill takes security seriously. All transactions are processed by Stripe, so our team will never see your
            credit card information. Please visit our privacy policy for more details on how we handle sensitive
            information.
          </Text>
        </YStack>
      </YStack>
    </YStack>
  );
};

export const PaymentStep = PaymentStepComponent;
